import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles/responsive.css';
import './styles/touch-accessibility.css';
import App from './AppSecure'; // Using secure version with guards
import reportWebVitals from './reportWebVitals';
import { AuthProvider } from './context/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from "@sentry/react";
import moment from 'moment-timezone';
import { BUSINESS_TIMEZONE } from './config/timezoneConfig';
import { installDeviceAlerts } from './owner/alerts/deviceAlerts';
import { createBrowserAlerts } from './owner/web/browserAlerts';
import { setBeforeSignOut } from './owner/shell/useOwnerSignOut';

// The owner pages' «this device» alerts on the web: browser alerts on a computer, the store app offered on a phone
// (owner/alerts/deviceAlerts.js); an owner's sign-out removes this browser first. Staff sign-out is untouched (it
// does not go through the owner sign-out). The store app installs its own channel (owner/app/index.js).
const browserAlerts = createBrowserAlerts();
installDeviceAlerts(browserAlerts);
setBeforeSignOut(browserAlerts.beforeSignOut);

// ⏰ Set global timezone for Saudi Arabia properties (all dates in property local time)
moment.tz.setDefault(BUSINESS_TIMEZONE);

// ✅ Initialize Sentry Error Monitoring
if (process.env.REACT_APP_SENTRY_ENABLED === 'true' && process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development',
    release: 'hostme-frontend@1.0.0',

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],

    // Performance monitoring
    tracesSampleRate: process.env.REACT_APP_SENTRY_ENVIRONMENT === 'production' ? 0.1 : 0,

    // Session Replay (only on errors)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Filter out expected errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Don't send authentication errors (expected)
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return null;
      }

      return event;
    },
  });

} else {
  // Sentry monitoring disabled
}

// Create React Query client with default configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      cacheTime: 10 * 60 * 1000, // 10 minutes - cached data kept in memory
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnReconnect: true, // Refetch when internet reconnects
      retry: 1, // Retry once on failure
    },
    mutations: {
      retry: 1, // Retry mutations once on failure
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div style={{padding: '20px', textAlign: 'center'}}>
      <h2>حدث خطأ غير متوقع</h2>
      <p>نعتذر عن الإزعاج. يرجى تحديث الصفحة أو الاتصال بالدعم.</p>
      <button onClick={() => window.location.reload()} style={{
        padding: '10px 20px',
        backgroundColor: '#007bff',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer'
      }}>
        تحديث الصفحة
      </button>
    </div>}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// NOTHING SERVICE-WORKER-RELATED RUNS AT BOOT, deliberately.
//
// This line used to call `unregisterAndCleanup()`, which destroyed the old caching worker on every
// load. That worker is gone. Its replacement (`public/sw.js`) is push-only and is registered from
// exactly one place: the button in Settings where an operator asks to turn notifications on
// (`utils/serviceWorker.js`). Registering here instead would fire a permission prompt on arrival,
// which is the fastest known route to a permanent `denied` — and would put a worker on the browsers
// of operators who never asked for one.
